const AUTH_READ = { action: 'read', subject: 'Auth' };
const SETTINGS_UPDATE = { action: 'update', subject: 'Settings' };

// Legacy backoffice role codes from pre-RBAC installs.
const LEGACY_ROLE_MAP = {
  101: 500,
  102: 120,
  103: 130,
  104: 125,
  105: 115,
};

const ROLE_ABILITIES = {
  1000: [
    { action: 'manage', subject: 'all' },
    { action: 'manage', subject: 'StaffSecurity' },
    AUTH_READ,
  ],
  500: [
    { action: 'read', subject: 'Dashboard' },
    { action: 'manage', subject: 'PlatformUsers' },
    { action: 'manage', subject: 'Fundraisers' },
    { action: 'manage', subject: 'Charities' },
    { action: 'manage', subject: 'Events' },
    { action: 'manage', subject: 'TicketEvents' },
    { action: 'manage', subject: 'Surveys' },
    { action: 'delete', subject: 'Surveys' },
    { action: 'read', subject: 'Transactions' },
    { action: 'export', subject: 'Transactions' },
    { action: 'read', subject: 'StaffUsers' },
    { action: 'create', subject: 'StaffUsers' },
    { action: 'update', subject: 'StaffUsers' },
    { action: 'delete', subject: 'StaffUsers' },
    SETTINGS_UPDATE,
    AUTH_READ,
  ],
  130: [
    { action: 'read', subject: 'Dashboard' },
    { action: 'read', subject: 'PlatformUsers' },
    { action: 'update', subject: 'PlatformUsers' },
    { action: 'manage', subject: 'Fundraisers' },
    { action: 'manage', subject: 'Charities' },
    { action: 'manage', subject: 'Events' },
    { action: 'manage', subject: 'TicketEvents' },
    { action: 'manage', subject: 'Surveys' },
    { action: 'delete', subject: 'Surveys' },
    { action: 'read', subject: 'Transactions' },
    { action: 'export', subject: 'Transactions' },
    SETTINGS_UPDATE,
    AUTH_READ,
  ],
  120: [
    { action: 'read', subject: 'Dashboard' },
    { action: 'read', subject: 'PlatformUsers' },
    { action: 'update', subject: 'PlatformUsers' },
    { action: 'read', subject: 'Fundraisers' },
    { action: 'update', subject: 'Fundraisers' },
    { action: 'read', subject: 'Charities' },
    { action: 'update', subject: 'Charities' },
    { action: 'read', subject: 'Events' },
    { action: 'update', subject: 'Events' },
    { action: 'read', subject: 'TicketEvents' },
    { action: 'update', subject: 'TicketEvents' },
    { action: 'read', subject: 'Surveys' },
    { action: 'export', subject: 'Surveys' },
    { action: 'delete', subject: 'Surveys' },
    { action: 'read', subject: 'Transactions' },
    SETTINGS_UPDATE,
    AUTH_READ,
  ],
  125: [
    { action: 'read', subject: 'Dashboard' },
    { action: 'read', subject: 'PlatformUsers' },
    { action: 'export', subject: 'PlatformUsers' },
    { action: 'read', subject: 'Fundraisers' },
    { action: 'read', subject: 'Charities' },
    { action: 'read', subject: 'Events' },
    { action: 'read', subject: 'TicketEvents' },
    { action: 'read', subject: 'Surveys' },
    { action: 'export', subject: 'Surveys' },
    { action: 'delete', subject: 'Surveys' },
    { action: 'read', subject: 'Transactions' },
    { action: 'export', subject: 'Transactions' },
    SETTINGS_UPDATE,
    AUTH_READ,
  ],
  115: [
    { action: 'read', subject: 'Dashboard' },
    { action: 'read', subject: 'PlatformUsers' },
    { action: 'read', subject: 'Fundraisers' },
    { action: 'read', subject: 'Charities' },
    { action: 'read', subject: 'Events' },
    { action: 'read', subject: 'TicketEvents' },
    { action: 'read', subject: 'Surveys' },
    { action: 'read', subject: 'Transactions' },
    SETTINGS_UPDATE,
    AUTH_READ,
  ],
};

export function normalizeStaffRole(role) {
  const numericRole = Number(role);
  if (Number.isNaN(numericRole)) {
    return 115;
  }

  return LEGACY_ROLE_MAP[numericRole] ?? numericRole;
}

/**
 * Mirrors user-center canResetStaffPassword.
 * Super admins reset every account, including other super admins.
 * Admins reset admins and lower roles, and cannot reset a super admin.
 */
export function canResetStaffPassword(callerRole, targetRole) {
  const caller = normalizeStaffRole(callerRole);
  const target = Number(targetRole);
  if (!Number.isFinite(caller)) return false;
  if (caller >= 1000) return true;
  if (caller >= 500 && caller < 1000) {
    return Number.isFinite(target) && target < 1000;
  }
  return false;
}

export function getAbilitiesForRole(role) {
  const normalizedRole = normalizeStaffRole(role);
  return ROLE_ABILITIES[normalizedRole] || ROLE_ABILITIES[115];
}

export function syncStoredStaffAbilities() {
  if (typeof localStorage === 'undefined') {
    return null;
  }

  const raw = localStorage.getItem('userData');
  if (!raw) {
    return null;
  }

  try {
    const userData = JSON.parse(raw);
    if (!userData?.role) {
      return null;
    }

    const ability = getAbilitiesForRole(userData.role);
    userData.ability = ability;
    localStorage.setItem('userData', JSON.stringify(userData));
    return ability;
  } catch {
    return null;
  }
}

export const STAFF_ROLE_OPTIONS = [
  { text: 'Super Admin', value: 1000 },
  { text: 'Admin', value: 500 },
  { text: 'Manager', value: 130 },
  { text: 'Support', value: 120 },
  { text: 'Analyst', value: 125 },
  { text: 'Auditor', value: 115 },
];

export function roleLabel(role) {
  const normalizedRole = normalizeStaffRole(role);
  const match = STAFF_ROLE_OPTIONS.find(item => item.value === normalizedRole);
  return match ? match.text : 'Staff';
}
